/**
 * Transfer speeds (owner and managers). Each person's real upload and download speed,
 * measured by their own browser at the end of every session (lib/transfer-stats.ts).
 * Built for "uploads are slow": is it the line, or the app?
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { PageHeader, Card } from '@/components/ui/Card';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

interface Speed { sessions: number; gigabytes: number; best_mbps?: number | null; typical_mbps?: number | null; last_at?: string | null }
interface Person { user_id: string; name: string; upload: Speed; download: Speed }
interface Session { name: string; direction: 'upload' | 'download'; files: number; megabytes: number; minutes: number; mbps: number; max_lanes?: number | null; finished_at: string }
interface Out { days: number; people: Person[]; recent: Session[] }

const RANGES = [7, 30, 90];

/** How long 1 GB takes at this speed, the unit people actually feel. */
function perGb(mbps?: number | null) {
  if (!mbps) return '';
  const s = 8000 / mbps;
  return s < 90 ? `${Math.round(s)}s per GB` : `${Math.round(s / 60)} min per GB`;
}

function tone(mbps?: number | null) {
  if (!mbps) return 'text-muted';
  if (mbps >= 40) return 'text-emerald-300';
  if (mbps >= 15) return 'text-slate-100';
  if (mbps >= 5) return 'text-amber-300';
  return 'text-red-300';
}

function SpeedCell({ s }: { s: Speed }) {
  if (!s.sessions) return <td className="px-3 py-2 text-muted text-xs">no sessions yet</td>;
  return (
    <td className="px-3 py-2 align-top">
      <div className={cn('font-semibold tabular-nums', tone(s.best_mbps))}>{s.best_mbps ?? '-'} Mbit/s <span className="text-xs font-normal text-muted">best</span></div>
      <div className="text-xs text-muted tabular-nums">
        {s.typical_mbps ?? '-'} typical, {perGb(s.best_mbps)}
      </div>
      <div className="text-xs text-muted tabular-nums">{s.sessions} session{s.sessions === 1 ? '' : 's'}, {s.gigabytes} GB</div>
    </td>
  );
}

export default function TransfersPage() {
  const [days, setDays] = useState(30);
  const q = useQuery({ queryKey: ['transfers', days], queryFn: async () => (await api.get<Out>('/transfers', { params: { days } })).data });

  return (
    <div>
      <PageHeader title="Transfer speeds" subtitle="Each person's real upload and download speed, measured by their own browser when a session finishes." />
      <div className="flex gap-2 mb-4">
        {RANGES.map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={cn('h-8 px-3 rounded-full text-xs border', d === days ? 'border-accent-500 text-accent-300 bg-accent-500/10' : 'border-slate-700 text-muted hover:text-muted-strong')}>
            Last {d} days
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-x-auto mb-6">
        {q.isLoading ? <div className="p-5 text-sm text-muted">Loading...</div> : !q.data?.people.length ? (
          <div className="p-5 text-sm text-muted">No sessions recorded yet. Speeds appear here after each upload or folder download of 5 MB or more.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted text-left">
              <tr className="border-b border-slate-800">
                <th className="px-3 py-2 font-medium">Person</th>
                <th className="px-3 py-2 font-medium"><span className="inline-flex items-center gap-1"><ArrowUp size={12} /> Upload</span></th>
                <th className="px-3 py-2 font-medium"><span className="inline-flex items-center gap-1"><ArrowDown size={12} /> Download</span></th>
              </tr>
            </thead>
            <tbody>
              {q.data.people.map((p) => (
                <tr key={p.user_id} className="border-b border-slate-800/60 last:border-0">
                  <td className="px-3 py-2 align-top font-medium text-muted-strong">{p.name}</td>
                  <SpeedCell s={p.upload} />
                  <SpeedCell s={p.download} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {!!q.data?.recent.length && (
        <Card className="p-0 overflow-x-auto">
          <div className="px-3 py-2 text-xs text-muted border-b border-slate-800">Latest sessions</div>
          <table className="w-full text-sm">
            <tbody>
              {q.data.recent.map((s, i) => (
                <tr key={i} className="border-b border-slate-800/60 last:border-0">
                  <td className="px-3 py-1.5 text-muted-strong">{s.name}</td>
                  <td className="px-3 py-1.5 text-xs text-muted">{s.direction === 'upload' ? 'upload' : 'download'}</td>
                  <td className="px-3 py-1.5 text-xs text-muted tabular-nums">{s.files} file{s.files === 1 ? '' : 's'}, {s.megabytes >= 1000 ? `${(s.megabytes / 1000).toFixed(1)} GB` : `${Math.round(s.megabytes)} MB`}</td>
                  <td className="px-3 py-1.5 text-xs text-muted tabular-nums">{s.minutes} min</td>
                  <td className={cn('px-3 py-1.5 font-medium tabular-nums', tone(s.mbps))}>{s.mbps} Mbit/s</td>
                  <td className="px-3 py-1.5 text-xs text-muted">{new Date(s.finished_at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
